import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import { FaAngleDoubleRight, FaChevronLeft, FaEdit, FaPlus, FaSave } from 'react-icons/fa';
import { useNavigate } from 'react-router';
import { AppContext } from '../AppContext';
import { getInfo, getSeccionesAdmin, saveSeccion, SeccionAdmin } from '../services/infoService';
import { PLATAFORMA_LABELS } from '../config/api';
import Swal from 'sweetalert2';
import '../assets/css/AdminSecciones.css';

const emptyForm = {
  id: '',
  seccion: '',
  seccion_destacada: '0',
  id_producto: '1',
};

const AdminSecciones: React.FC = () => {
  const navigate = useNavigate();
  const context = useContext(AppContext);
  const [secciones, setSecciones] = useState<SeccionAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);

  const plataforma = context?.plataforma ?? 1;
  const plataformaLabel = PLATAFORMA_LABELS[plataforma];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const hash = localStorage.getItem('hash');
        if (context?.data.secciones.length === 0) {
          await getInfo(context, hash, plataforma);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  }, [context, plataforma]);

  const loadSecciones = async () => {
    setLoading(true);
    try {
      const data = await getSeccionesAdmin(plataforma);
      setSecciones(data);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error instanceof Error ? error.message : 'No se pudieron cargar las secciones.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSecciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plataforma]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditing(false);
  };

  const handleEdit = (seccion: SeccionAdmin) => {
    setForm({
      id: String(seccion.id),
      seccion: seccion.seccion,
      seccion_destacada: String(seccion.seccion_destacada),
      id_producto: String(seccion.id_producto),
    });
    setEditing(true);
  };

  const handleSubmit = async () => {
    if (!form.seccion.trim()) {
      Swal.fire({ icon: 'warning', title: 'Campo requerido', text: 'Ingrese el nombre de la sección.' });
      return;
    }

    setSaving(true);
    try {
      await saveSeccion({
        id: form.id ? Number(form.id) : undefined,
        seccion: form.seccion.trim(),
        seccion_destacada: Number(form.seccion_destacada),
        id_producto: Number(form.id_producto),
        id_plataforma: plataforma,
      });

      const hash = localStorage.getItem('hash');
      if (context) {
        await getInfo(context, hash, plataforma);
      }

      await loadSecciones();
      resetForm();

      Swal.fire({
        icon: 'success',
        title: editing ? 'Sección actualizada' : 'Sección creada',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar',
        text: error instanceof Error ? error.message : 'No se pudo guardar la sección.',
      });
    } finally {
      setSaving(false);
    }
  };

  console.log(secciones);

  return (
    <div className="admin-secciones-container fadeIn">
      <div className="view">
        <div className="flex items-center mb-4">
          <div className="items-center gap-2 hidden sm:block">
            <Button variant="text" className="!rounded-3xl " onClick={() => navigate(-1)} title='volver'>
              <FaChevronLeft className="i" style={{ marginRight: '5px' }} />
              Volver al Inicio
            </Button>
          </div>
          <div className="flex items-center gap-2 sm:hidden">
            <Button variant="text" className="!rounded-3xl sm:hidden " onClick={() => navigate(-1)} title='volver'>
              <FaChevronLeft className="i" style={{ marginRight: '5px' }} />
            </Button>
          </div>
          <div className="flex items-center">
            <FaAngleDoubleRight className="i mr-2" size={12} />
            <h2 className="!mb-0">Administración de secciones</h2>
          </div>
        </div>
      </div>


      <p className="admin-subtitle">
        Plataforma activa: <strong>{plataformaLabel}</strong> — Gestione las secciones donde se organizan los vínculos y videos.
      </p>

      <Paper className="admin-form-card" elevation={2}>
        <h3>{editing ? 'Editar sección' : 'Nueva sección'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <TextField
            label="Nombre de la sección"
            value={form.seccion}
            onChange={(e) => setForm({ ...form, seccion: e.target.value })}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>¿Sección destacada?</InputLabel>
            <Select
              value={form.seccion_destacada}
              label="¿Sección destacada?"
              onChange={(e) => setForm({ ...form, seccion_destacada: e.target.value })}
            >
              <MenuItem value="1">Sí</MenuItem>
              <MenuItem value="0">No</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Producto</InputLabel>
            <Select
              value={form.id_producto}
              label="Producto"
              onChange={(e) => setForm({ ...form, id_producto: e.target.value })}
            >
              <MenuItem value="1">Colegio</MenuItem>
              <MenuItem value="2">Instituto</MenuItem>
            </Select>
          </FormControl>
        </div>
        <div className="mt-6">
          <Button variant="contained" className="!rounded-3xl" style={editing ? { backgroundColor: "#16A085" } : {}} onClick={handleSubmit} disabled={saving}>
            {editing ? 'Actualizar' : 'Crear sección'}
            {editing ? <FaSave style={{ marginLeft: 8 }} /> : <FaPlus style={{ marginLeft: 8 }} />}
          </Button>
          {editing && (
            <Button variant="outlined" className="!rounded-3xl" onClick={resetForm} disabled={saving}>
              Cancelar edición
            </Button>
          )}
        </div>
      </Paper>

      {loading ? (
        <CircularProgress sx={{ mt: 4 }} />
      ) : (
        <TableContainer component={Paper} className="admin-table" elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">ID</TableCell>
                <TableCell align="center">Sección</TableCell>
                <TableCell align="center">Destacada</TableCell>
                <TableCell align="center">Producto</TableCell>
                <TableCell align="center">Editar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {secciones.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No hay secciones para {plataformaLabel}. Cree la primera arriba.
                  </TableCell>
                </TableRow>
              ) : (
                secciones.map((seccion) => (
                  <TableRow key={seccion.id} hover>
                    <TableCell>{seccion.id}</TableCell>
                    <TableCell>{seccion.seccion}</TableCell>
                    <TableCell align="center">
                      {Number(seccion.seccion_destacada) === 1 ? (
                        <Chip label="Destacada" size="small" color="primary" />
                      ) : (
                        <Chip label="Normal" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {Number(seccion.id_producto) === 1 ? 'Colegio' : 'Instituto'}
                    </TableCell>
                    <TableCell align="center" className=''>
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<FaEdit />}
                        className="!rounded-3xl"
                        style={{ color: "#16A085", }}
                        onClick={() => handleEdit(seccion)}
                      >
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default AdminSecciones;
